import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Crown, LogOut, Package } from "lucide-react";
import { ShopLayout } from "../../components/shop/ShopLayout";
import { fmt, STATUS_LABELS } from "../../lib/shopApi";
import { StatusTimeline, OrderItemsList } from "./TrackOrderPage";

const AccountDashboardPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("cc_customer_token");
    if (!token) {
      navigate("/account/login");
      return;
    }
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/account/me/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) {
          localStorage.removeItem("cc_customer_token");
          navigate("/account/login");
        }
      });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("cc_customer_token");
    navigate("/shop");
  };

  return (
    <ShopLayout title="My Account | Loctician Carlocs">
      <div className="max-w-3xl mx-auto px-5 py-12 md:py-16" data-testid="account-dashboard">
        {!data ? (
          <p className="text-center text-[#F5EFE3]/40 text-sm py-20">Loading your account…</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
              <div>
                <p className="text-[#C9A24B] text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2">
                  <Crown className="w-4 h-4" /> My Account
                </p>
                <h1 className="font-serif text-3xl md:text-4xl">Hello, <span className="italic text-[#C9A24B]">{data.customer.name.split(" ")[0]}</span></h1>
              </div>
              <button
                onClick={logout}
                data-testid="account-logout-btn"
                className="flex items-center gap-2 text-[#F5EFE3]/50 hover:text-[#C9A24B] text-xs uppercase tracking-widest transition-colors"
              >
                <LogOut className="w-4 h-4" /> Sign Out
              </button>
            </div>

            {data.orders.length === 0 ? (
              <div className="text-center py-16 bg-[#232323] border border-[#C9A24B]/10 rounded-2xl" data-testid="account-no-orders">
                <Package className="w-9 h-9 text-[#C9A24B]/40 mx-auto mb-4" />
                <p className="text-[#F5EFE3]/50 mb-6">No orders yet on this account.</p>
                <Link to="/shop" className="inline-block bg-[#C9A24B] text-[#1C1C1C] px-8 py-3.5 rounded-full text-xs font-bold uppercase tracking-widest">
                  Browse the Shop
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {data.orders.map((o) => (
                  <div key={o.id} className="bg-[#232323] border border-[#C9A24B]/10 rounded-2xl overflow-hidden" data-testid={`account-order-${o.order_number}`}>
                    <button
                      onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                      className="w-full p-5 flex flex-wrap items-center gap-3 text-left"
                      data-testid={`account-order-toggle-${o.order_number}`}
                    >
                      <div className="flex-1 min-w-[150px]">
                        <p className="font-serif text-lg text-[#C9A24B]">{o.order_number}</p>
                        <p className="text-[#F5EFE3]/45 text-xs mt-0.5">
                          {o.created_at ? new Date(o.created_at).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" }) : ""} · {fmt(o.total_cents)}
                        </p>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-[#C9A24B]/40 text-[#C9A24B]">
                        {STATUS_LABELS[o.status] || o.status.replace(/_/g, " ")}
                      </span>
                    </button>
                    {expanded === o.id && (
                      <div className="px-5 pb-6 border-t border-[#C9A24B]/10 pt-5 space-y-5">
                        {o.has_made_to_order && (
                          <p className="border border-[#C9A24B]/40 bg-[#C9A24B]/10 text-[#C9A24B] text-xs leading-relaxed rounded-xl px-4 py-3">
                            Includes made-to-order locs extensions — handmade in {o.lead_time_text || "7-15 days"}, then your delivery begins.
                          </p>
                        )}
                        <OrderItemsList items={o.items} />
                        <StatusTimeline order={o} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </ShopLayout>
  );
};

export default AccountDashboardPage;
